import { Fragment, useEffect, useState, type FormEvent } from 'react';
import {
  createStage,
  deleteStage,
  listStages,
  reorderStages,
  updateStage,
  type Stage,
} from '../api/stages';
import { getTripRoute, type TripRoute } from '../api/trips';
import { HttpError } from '../api/client';
import { TripMap } from './TripMap';
import { AddressSearch } from './AddressSearch';
import { AddressBookPicker } from './AddressBookPicker';
import { PoiPicker } from './PoiPicker';
import { StageWeatherBadge } from './StageWeatherBadge';
import { PhoneLink } from './PhoneLink';
import { LodgingFields, type LodgingFormState } from './LodgingFields';
import {
  LODGING_TYPE_LABELS,
  type LodgingType,
  type StageLodgingDto,
} from '@camper-diary/shared';
import { reverseGeocode, type Geo } from '../api/geocoding';
import { openNavigation } from '../utils/navigate';
import { parseCoordinate, normalizeCoordinateInput } from '../utils/coordinates';

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} min`;
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm > 0 ? `${hh}h ${mm}m` : `${hh}h`;
}

function emptyLodgingForm(): LodgingFormState {
  return { name: '', type: '', phone: '', url: '', lat: '', lon: '', notes: '', useForRouting: false };
}

function lodgingFromStage(l: StageLodgingDto): LodgingFormState {
  return {
    name: l.name ?? '',
    type: l.type ?? '',
    phone: l.phone ?? '',
    url: l.url ?? '',
    lat: l.lat !== null ? l.lat.toString() : '',
    lon: l.lon !== null ? l.lon.toString() : '',
    notes: l.notes ?? '',
    useForRouting: l.useForRouting,
  };
}

/**
 * Converte lo stato del form Alloggio in payload API.
 * Ritorna `null` se l'utente non ha compilato nulla (= alloggio assente),
 * altrimenti l'oggetto da inviare. lat/lon vengono inviate solo se ENTRAMBE
 * valide; un solo valore inserito viene scartato.
 */
function lodgingPayload(f: LodgingFormState): Partial<StageLodgingDto> | null {
  const name = f.name.trim();
  const phone = f.phone.trim();
  const url = f.url.trim();
  const notes = f.notes.trim();
  const type = f.type ? (f.type as LodgingType) : null;
  const latNum = f.lat.trim() ? parseCoordinate(f.lat) : null;
  const lonNum = f.lon.trim() ? parseCoordinate(f.lon) : null;
  const validCoords =
    latNum !== null && lonNum !== null &&
    latNum >= -90 && latNum <= 90 && lonNum >= -180 && lonNum <= 180;
  if (!name && !type && !phone && !url && !validCoords && !notes) return null;
  return {
    name: name || null,
    type: type ?? null,
    phone: phone || null,
    url: url || null,
    lat: validCoords ? latNum : null,
    lon: validCoords ? lonNum : null,
    notes: notes || null,
    // il flag ha senso solo con coordinate alloggio valide
    useForRouting: validCoords ? f.useForRouting : false,
  };
}

type Mode = { kind: 'idle' } | { kind: 'creating' } | { kind: 'editing'; stage: Stage };

export function StagesSection({ tripId }: { tripId: string }) {
  const [stages, setStages] = useState<Stage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);

  // Coordinate + indirizzo del form sollevati qui, per poterli aggiornare
  // sia dal click su mappa (reverse geocoding) sia dalla ricerca (autocomplete)
  const [formLat, setFormLat] = useState<string>('');
  const [formLon, setFormLon] = useState<string>('');
  const [formAddress, setFormAddress] = useState<string>('');

  // Route stradale OSRM per disegnare il percorso vero sulla mappa
  const [route, setRoute] = useState<TripRoute>(null);
  const [routeLoading, setRouteLoading] = useState(false);

  async function refresh() {
    try {
      const list = await listStages(tripId);
      setStages(list);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento tappe');
    }
  }

  async function refreshRoute() {
    setRouteLoading(true);
    try {
      const r = await getTripRoute(tripId);
      setRoute(r);
    } catch {
      setRoute(null);
    } finally {
      setRouteLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    void refreshRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  // Refetch route quando cambiano numero/posizione delle tappe
  const stageRouteKey = stages
    ? stages
        .map(
          (s) =>
            `${s.id}:${s.lat}:${s.lon}:${s.lodging.useForRouting ? 1 : 0}:${s.lodging.lat ?? ''}:${s.lodging.lon ?? ''}`,
        )
        .join('|')
    : '';
  useEffect(() => {
    if (!stages) return;
    if (stages.length < 2) {
      setRoute(null);
      return;
    }
    void refreshRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageRouteKey]);

  function startCreate() {
    setFormLat('');
    setFormLon('');
    setFormAddress('');
    setMode({ kind: 'creating' });
  }

  function startEdit(stage: Stage) {
    setFormLat(stage.lat.toString());
    setFormLon(stage.lon.toString());
    setFormAddress(stage.address ?? '');
    setMode({ kind: 'editing', stage });
  }

  /** Pick da AddressSearch: lat/lon e indirizzo arrivano dal risultato Nominatim */
  function onAddressPick(result: Geo) {
    setFormLat(result.lat.toFixed(6));
    setFormLon(result.lon.toFixed(6));
    setFormAddress(result.displayName);
  }

  /** Pick da rubrica: riempie lat/lon/address; il titolo viene gestito dentro il form. */
  function onAddressBookPick(entry: { lat: number; lon: number; address: string | null }) {
    setFormLat(entry.lat.toFixed(6));
    setFormLon(entry.lon.toFixed(6));
    setFormAddress(entry.address ?? '');
  }

  /** Pick da POI: riempie lat/lon/address; titolo gestito nel form. */
  function onPoiPick(p: { lat: number; lon: number; address: string | null }) {
    setFormLat(p.lat.toFixed(6));
    setFormLon(p.lon.toFixed(6));
    setFormAddress(p.address ?? '');
  }

  function cancelForm() {
    setMode({ kind: 'idle' });
  }

  async function onDelete(stage: Stage) {
    if (!confirm(`Eliminare la tappa "${stage.title}"?`)) return;
    setBusy(true);
    try {
      await deleteStage(tripId, stage.id);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore eliminazione');
    } finally {
      setBusy(false);
    }
  }

  async function onMove(stage: Stage, dir: -1 | 1) {
    if (!stages) return;
    const idx = stages.findIndex((s) => s.id === stage.id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= stages.length) return;
    const ids = stages.map((s) => s.id);
    [ids[idx], ids[target]] = [ids[target] as string, ids[idx] as string];
    setBusy(true);
    try {
      const updated = await reorderStages(tripId, ids);
      setStages(updated);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore riordino');
    } finally {
      setBusy(false);
    }
  }

  // Posizione corrente nel form (per il marker temporaneo sulla mappa)
  const pickPos = (() => {
    if (mode.kind === 'idle') return null;
    const lat = parseCoordinate(formLat);
    const lon = parseCoordinate(formLon);
    if (lat === null || lon === null) return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
    return { lat, lon };
  })();

  const onMapPick =
    mode.kind === 'idle'
      ? undefined
      : (lat: number, lon: number) => {
          setFormLat(lat.toFixed(6));
          setFormLon(lon.toFixed(6));
          // reverse geocode in background; aggiorna address solo se vuoto
          // (non sovrascriviamo quanto l'utente ha già scritto)
          void (async () => {
            try {
              const r = await reverseGeocode(lat, lon);
              if (r) {
                setFormAddress((cur) => (cur.trim() === '' ? r.displayName : cur));
              }
            } catch {
              // silenzioso: il reverse è best-effort
            }
          })();
        };

  return (
    <section className="stages-section">
      <div className="section-head">
        <h2>Tappe</h2>
        {mode.kind === 'idle' && (
          <button className="btn btn-primary" onClick={startCreate} disabled={busy}>
            + Aggiungi tappa
          </button>
        )}
      </div>

      {error && <p className="err">{error}</p>}

      {/* Mappa: sempre visibile se c'è almeno una tappa o il form è aperto */}
      {stages && (stages.length > 0 || mode.kind !== 'idle') && (
        <TripMap
          tripId={tripId}
          stages={stages}
          pick={pickPos}
          onPick={onMapPick}
          routePolyline={route?.polyline ?? null}
        />
      )}

      {mode.kind === 'creating' && (
        <StageForm
          lat={formLat}
          lon={formLon}
          onLatChange={setFormLat}
          onLonChange={setFormLon}
          address={formAddress}
          onAddressChange={setFormAddress}
          onAddressPick={onAddressPick}
          onAddressBookPick={onAddressBookPick}
          onPoiPick={onPoiPick}
          onCancel={cancelForm}
          onSubmit={async (data) => {
            await createStage(tripId, data);
            cancelForm();
            await refresh();
          }}
        />
      )}

      {mode.kind === 'editing' && (
        <StageForm
          initial={mode.stage}
          lat={formLat}
          lon={formLon}
          onLatChange={setFormLat}
          onLonChange={setFormLon}
          address={formAddress}
          onAddressChange={setFormAddress}
          onAddressPick={onAddressPick}
          onAddressBookPick={onAddressBookPick}
          onPoiPick={onPoiPick}
          onCancel={cancelForm}
          onSubmit={async (data) => {
            await updateStage(tripId, mode.stage.id, data);
            cancelForm();
            await refresh();
          }}
        />
      )}

      {!stages && <p className="muted">Caricamento…</p>}

      {stages && stages.length === 0 && mode.kind === 'idle' && (
        <p className="muted">Nessuna tappa. Aggiungine una.</p>
      )}

      {stages && stages.length > 0 && (
        <ol className="stage-list">
          {stages.map((s, idx) => {
            const leg = idx < stages.length - 1 ? route?.legs?.[idx] : undefined;
            return (
            <Fragment key={s.id}>
            <li className="stage-item">
              <div className="stage-order">
                <button
                  type="button"
                  className="arrow"
                  disabled={busy || idx === 0}
                  onClick={() => onMove(s, -1)}
                  aria-label="Sposta su"
                  title="Sposta su"
                >
                  ▲
                </button>
                <span className="num">{idx + 1}</span>
                <button
                  type="button"
                  className="arrow"
                  disabled={busy || idx === stages.length - 1}
                  onClick={() => onMove(s, 1)}
                  aria-label="Sposta giù"
                  title="Sposta giù"
                >
                  ▼
                </button>
              </div>
              <div className="stage-body">
                <h3 className="cd-title-upper">{s.title}</h3>
                {s.address && <p className="muted">{s.address}</p>}
                <p className="coords">
                  {s.lat.toFixed(5)}, {s.lon.toFixed(5)}
                  {s.plannedDate && ` · ${formatDate(s.plannedDate)}`}
                </p>
                <StageWeatherBadge
                  tripId={tripId}
                  stageId={s.id}
                  plannedDate={s.plannedDate}
                />
                {s.info && <p className="info">{s.info}</p>}
                <StageLodgingView lodging={s.lodging} busy={busy} />
              </div>
              <div className="stage-actions">
                <button
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => openNavigation(s.lat, s.lon, s.title)}
                >
                  Naviga
                </button>
                <button
                  className="btn btn-ghost"
                  disabled={busy}
                  onClick={() => startEdit(s)}
                >
                  Modifica
                </button>
                <button
                  className="btn btn-danger"
                  disabled={busy}
                  onClick={() => void onDelete(s)}
                >
                  Elimina
                </button>
              </div>
            </li>
            {idx < stages.length - 1 && (
              <li className="stage-leg" aria-hidden="true">
                {routeLoading ? (
                  <span className="muted small">↓ calcolo distanza…</span>
                ) : leg ? (
                  <span className="leg-text">
                    ↓ <strong>{Math.round(leg.distanceKm)} km</strong> ·{' '}
                    {formatDuration(leg.durationMinutes)}
                  </span>
                ) : (
                  <span className="muted small">↓ —</span>
                )}
              </li>
            )}
            </Fragment>
            );
          })}
        </ol>
      )}

      {route && stages && stages.length >= 2 && (
        <p className="route-total muted">
          Totale stradale: <strong>~{Math.round(route.distanceKm)} km</strong> ·{' '}
          {formatHours(route.durationHours)} di guida
        </p>
      )}
    </section>
  );
}

type StageFormData = {
  title: string;
  info: string | null;
  lat: number;
  lon: number;
  address: string | null;
  plannedDate: string | null;
  lodging: Partial<StageLodgingDto> | null;
};

function StageForm({
  initial,
  lat,
  lon,
  onLatChange,
  onLonChange,
  address,
  onAddressChange,
  onAddressPick,
  onAddressBookPick,
  onPoiPick,
  onSubmit,
  onCancel,
}: {
  initial?: Stage;
  lat: string;
  lon: string;
  onLatChange: (v: string) => void;
  onLonChange: (v: string) => void;
  address: string;
  onAddressChange: (v: string) => void;
  onAddressPick: (result: Geo) => void;
  onAddressBookPick: (entry: { label: string; lat: number; lon: number; address: string | null }) => void;
  onPoiPick: (poi: { name: string; lat: number; lon: number; address: string | null }) => void;
  onSubmit: (data: StageFormData) => Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [info, setInfo] = useState(initial?.info ?? '');
  const [plannedDate, setPlannedDate] = useState(initial?.plannedDate ?? '');
  const [lodging, setLodging] = useState<LodgingFormState>(
    initial ? lodgingFromStage(initial.lodging) : emptyLodgingForm(),
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const latNum = parseCoordinate(lat);
    const lonNum = parseCoordinate(lon);
    if (latNum === null || latNum < -90 || latNum > 90) {
      setError('Latitudine non valida (deve essere tra -90 e 90)');
      return;
    }
    if (lonNum === null || lonNum < -180 || lonNum > 180) {
      setError('Longitudine non valida (deve essere tra -180 e 180)');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        info: info.trim() || null,
        lat: latNum,
        lon: lonNum,
        address: address.trim() || null,
        plannedDate: plannedDate || null,
        lodging: lodgingPayload(lodging),
      });
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.apiError.message);
      } else {
        setError('Errore di connessione');
      }
      setSubmitting(false);
    }
  }

  return (
    <form className="form-card stage-form" onSubmit={handleSubmit}>
      <div className="form-toolbar">
        <AddressBookPicker
          onPick={(entry) => {
            if (!title.trim()) setTitle(entry.label);
            onAddressBookPick(entry);
          }}
        />
        <PoiPicker
          onPick={(p) => {
            if (!title.trim()) setTitle(p.name);
            onPoiPick(p);
          }}
        />
      </div>

      <label>
        <span>Titolo *</span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          autoFocus
          maxLength={200}
        />
      </label>

      <label>
        <span>Cerca luogo / indirizzo</span>
        <AddressSearch value={address} onChange={onAddressChange} onPick={onAddressPick} />
      </label>

      <div className="row">
        <label>
          <span>Latitudine *</span>
          <input
            type="text"
            inputMode="decimal"
            value={lat}
            onChange={(e) => onLatChange(e.target.value)}
            onBlur={(e) => onLatChange(normalizeCoordinateInput(e.target.value))}
            required
            placeholder="46.5405"
          />
        </label>
        <label>
          <span>Longitudine *</span>
          <input
            type="text"
            inputMode="decimal"
            value={lon}
            onChange={(e) => onLonChange(e.target.value)}
            onBlur={(e) => onLonChange(normalizeCoordinateInput(e.target.value))}
            required
            placeholder="12.1357"
          />
        </label>
      </div>
      <p className="muted form-hint">
        Cerca un luogo qui sopra <em>oppure</em> clicca direttamente sulla mappa per scegliere la
        posizione (le coordinate si compilano automaticamente). Puoi anche incollare valori in
        formato <code>44°49'46.36"N</code> — verranno convertiti in decimali.
      </p>

      <label>
        <span>Data prevista</span>
        <input
          type="date"
          value={plannedDate}
          onChange={(e) => setPlannedDate(e.target.value)}
        />
      </label>

      <label>
        <span>Info utili (descrizione, servizi, recensione personale…)</span>
        <textarea
          rows={5}
          value={info}
          onChange={(e) => setInfo(e.target.value)}
          maxLength={5000}
        />
      </label>

      <LodgingFields
        value={lodging}
        onChange={setLodging}
        stageLat={lat}
        stageLon={lon}
      />


      {error && <p className="err">{error}</p>}

      <div className="actions">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          Annulla
        </button>
        <button type="submit" disabled={submitting || !title.trim()}>
          {submitting ? 'Salvataggio…' : initial ? 'Salva modifiche' : 'Crea tappa'}
        </button>
      </div>
    </form>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Rende il blocco "Alloggio" sotto una tappa nella lista. Nasconde tutto se
 * nessun campo è valorizzato. Il bottone "Naviga" appare solo se sono presenti
 * sia lat che lon dell'alloggio (perché altrimenti non c'è dove andare).
 */
export function StageLodgingView({
  lodging,
  busy,
}: {
  lodging: StageLodgingDto;
  busy?: boolean;
}) {
  const hasAny =
    lodging.name ||
    lodging.type ||
    lodging.phone ||
    lodging.url ||
    (lodging.lat !== null && lodging.lon !== null) ||
    lodging.notes;
  if (!hasAny) return null;
  const typeMeta = lodging.type ? LODGING_TYPE_LABELS[lodging.type] : null;
  const navLabel = lodging.name ?? typeMeta?.label ?? 'Alloggio';
  return (
    <div className="stage-lodging">
      <div className="stage-lodging-head">
        <span className="stage-lodging-icon" aria-hidden="true">🛏️</span>
        <strong>{lodging.name ?? 'Alloggio'}</strong>
        {typeMeta && (
          <span className="stage-lodging-type muted">
            {typeMeta.icon} {typeMeta.label}
          </span>
        )}
        {lodging.useForRouting && lodging.lat !== null && lodging.lon !== null && (
          <span
            className="stage-lodging-routing-badge"
            title="I km verso/da questa tappa sono calcolati su questo alloggio"
          >
            📏 usato per i km
          </span>
        )}
      </div>
      {lodging.phone && (
        <p className="phone">
          <PhoneLink phone={lodging.phone} />
        </p>
      )}
      {lodging.url && (
        <p className="stage-lodging-url">
          <a href={lodging.url} target="_blank" rel="noopener noreferrer">
            {lodging.url}
          </a>
        </p>
      )}
      {lodging.notes && <p className="info">{lodging.notes}</p>}
      {lodging.lat !== null && lodging.lon !== null && (
        <div className="stage-lodging-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy}
            onClick={() => openNavigation(lodging.lat!, lodging.lon!, navLabel)}
            title="Naviga all'alloggio"
          >
            Naviga all'alloggio
          </button>
        </div>
      )}
    </div>
  );
}
